// src/Pages/BlitzPay/t2a/successT2A.js
// T2A Gift Payment Success — completely separate from deposit/balance top-up flows.
//
// WHAT THIS IS:
//   The sender paid externally (card via Stripe, PayPal) to fund transfers TO other people.
//   Their own Blitz balance is NOT touched — they bought credits for recipients.
//   This page confirms that payment happened and shows who received money.
//
// WHAT THIS IS NOT:
//   This is NOT a deposit. The sender's balance does NOT increase.
//   Do NOT use handleStripeSuccess or any deposit endpoint here.
//
// ARCHITECTURE:
//   1. Stripe redirects to /payment/success?provider=stripe&type=t2a_gift&session_id=cs_...
//   2. App.js routes that to PaymentSuccessRouter which checks type=t2a_gift → renders T2APaymentSuccess
//   3. This component calls client.balanceLedger.t2aGiftSuccess (POST /api/gu-balance/t2a-gift/success)
//   4. Backend verifies payment with Stripe, credits recipients via execute_gift_confirm (idempotent)
//   5. This page just shows the result

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, CircularProgress, Divider, Chip, Avatar,
  Fade,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Error as ErrorIcon,
  Send as SendIcon,
  AccountBalanceWallet as WalletIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { alpha, useTheme, keyframes } from '@mui/material/styles';
import client from '../../../API';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.05); }
`;

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

// ─── Loading Screen ───────────────────────────────────────────────────────────
function T2ALoading() {
  const theme = useTheme();
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      minHeight: '70vh', gap: 3,
      animation: `${fadeUp} 0.3s ease`,
    }}>
      <Box sx={{
        width: 80, height: 80, borderRadius: '50%',
        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
        animation: `${pulse} 1.6s ease-in-out infinite`,
      }}>
        <SendIcon sx={{ color: 'white', fontSize: 36 }} />
      </Box>
      <CircularProgress size={28} thickness={3} />
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Confirming your transfer…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verifying payment and crediting your recipients
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Error Screen ─────────────────────────────────────────────────────────────
function T2AError({ message }) {
  const theme = useTheme();
  const navigate = useNavigate();
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      minHeight: '70vh', gap: 3, px: 3,
      animation: `${fadeUp} 0.3s ease`,
    }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: '50%',
        bgcolor: alpha(theme.palette.error.main, 0.1),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <ErrorIcon sx={{ fontSize: 40, color: 'error.main' }} />
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={800} color="error.main" gutterBottom>
          Something went wrong
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
          {message}
        </Typography>
        {message?.toLowerCase().includes('contact support') && (
          <Box sx={{
            mt: 2, p: 2, borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}>
            <Typography variant="caption" color="warning.dark">
              ⚠️ If you were charged, your money is safe. Funds will be credited
              to recipients within 24 hours or refunded. Contact support@useblitz.co
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/blitzpay')}>
          Back to Blitz T2
        </Button>
      </Box>
    </Box>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────
function T2ASuccess({ data }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const legs = data.legs || [];
  const creditedCount = legs.filter((l) => l.status === 'credited').length;
  const pendingCount  = legs.filter((l) => l.status !== 'credited').length;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      minHeight: '70vh', py: 4, px: 2,
      animation: `${fadeUp} 0.4s ease`,
    }}>
      <Paper elevation={0} sx={{
        maxWidth: 560, width: '100%',
        border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
        borderRadius: 4, overflow: 'hidden',
      }}>
        {/* Header */}
        <Box sx={{
          p: 4, textAlign: 'center',
          background: `linear-gradient(135deg,
            ${theme.palette.success.dark} 0%,
            ${theme.palette.success.main} 100%)`,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', top: -40, right: -40,
            width: 180, height: 180, borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#fff', 0.15)} 0%, transparent 70%)`,
          }} />
          <CheckCircle sx={{ fontSize: 64, color: 'white', mb: 1.5 }} />
          <Typography variant="h4" fontWeight={900} color="white" sx={{ letterSpacing: '-0.03em', mb: 0.5 }}>
            Transfer Sent!
          </Typography>
          <Typography variant="body1" sx={{ color: alpha('#fff', 0.85), fontWeight: 500 }}>
            {creditedCount} recipient{creditedCount !== 1 ? 's' : ''} have been credited
          </Typography>
        </Box>

        {/* Financial summary */}
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
          <Typography variant="overline" color="text.disabled" sx={{ fontSize: '0.65rem', letterSpacing: '0.1em', fontWeight: 700 }}>
            Payment Summary
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReceiptIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">You paid (card charge)</Typography>
              </Box>
              <Typography variant="body2" fontWeight={700}>{fmt(data.total_sender_pays)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="body2" color="text.secondary">Agreed transfer total</Typography>
              </Box>
              <Typography variant="body2" fontWeight={700}>{fmt(data.total_agreed)}</Typography>
            </Box>
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <WalletIcon sx={{ fontSize: 15, color: 'info.main' }} />
              <Typography variant="caption" color="info.dark" sx={{ fontWeight: 600 }}>
                Your Blitz balance was not charged — your card was billed directly to fund recipients.
              </Typography>
            </Box>
            {data.batch_ref && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.5, py: 0.75 }}>
                <Typography variant="caption" color="text.disabled">Reference</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                  {data.batch_ref}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Recipients */}
        {legs.length > 0 && (
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
            <Typography variant="overline" color="text.disabled" sx={{ fontSize: '0.65rem', letterSpacing: '0.1em', fontWeight: 700 }}>
              Recipients Credited
            </Typography>
            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {legs.map((leg, i) => (
                <Fade in key={leg.leg_ref || i} style={{ transitionDelay: `${i * 60}ms` }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.25, borderRadius: 2,
                    bgcolor: alpha(leg.status === 'credited' ? theme.palette.success.main : theme.palette.warning.main, 0.06),
                    border: `1px solid ${alpha(leg.status === 'credited' ? theme.palette.success.main : theme.palette.warning.main, 0.2)}`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <Avatar sx={{
                        width: 32, height: 32, fontSize: '0.8rem', fontWeight: 700,
                        bgcolor: alpha(leg.status === 'credited' ? theme.palette.success.main : theme.palette.warning.main, 0.15),
                        color: leg.status === 'credited' ? 'success.main' : 'warning.main',
                      }}>
                        {i + 1}
                      </Avatar>
                      <Box>
                        <Chip
                          size="small"
                          label={leg.status === 'credited' ? '✓ Credited' : leg.status}
                          color={leg.status === 'credited' ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }}
                        />
                        {leg.leg_ref && (
                          <Typography variant="caption" display="block" color="text.disabled" sx={{ fontSize: '0.6rem', mt: 0.25 }}>
                            {leg.leg_ref}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Typography variant="body2" fontWeight={800} color="success.main">
                      {fmt(leg.recipient_receives)}
                    </Typography>
                  </Box>
                </Fade>
              ))}
            </Box>
            {pendingCount > 0 && (
              <Box sx={{
                mt: 1.5, p: 1.5, borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.06),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}>
                <Typography variant="caption" color="warning.dark">
                  ⏳ {pendingCount} transfer{pendingCount !== 1 ? 's are' : ' is'} pending.
                  Email/SMS recipients will receive claim instructions shortly.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Status message when crediting is still in progress */}
        {data.status !== 'completed' && legs.length === 0 && (
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
            <Box sx={{
              p: 2, borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              textAlign: 'center',
            }}>
              <Typography variant="body2" color="info.main" fontWeight={600} gutterBottom>
                Payment confirmed ✓
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Your payment was accepted. Recipients are being credited now.
                This usually completes within seconds.
              </Typography>
            </Box>
          </Box>
        )}

        {/* CTA */}
        <Box sx={{ p: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined" fullWidth
            onClick={() => navigate('/dashboard')}
            sx={{ borderRadius: 2.5, textTransform: 'none', fontWeight: 700 }}
          >
            Dashboard
          </Button>
          <Button
            variant="contained" fullWidth
            endIcon={<ArrowIcon />}
            onClick={() => navigate('/blitzpay')}
            sx={{
              borderRadius: 2.5, textTransform: 'none', fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
              '&:hover': { transform: 'translateY(-1px)' }, transition: 'all 0.2s ease',
            }}
          >
            Send Another
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Cancelled Screen ─────────────────────────────────────────────────────────
export function T2APaymentCancelled() {
  const navigate = useNavigate();
  const theme = useTheme();
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      minHeight: '70vh', gap: 3, px: 3,
      animation: `${fadeUp} 0.3s ease`,
    }}>
      <Box sx={{
        width: 72, height: 72, borderRadius: '50%',
        bgcolor: alpha(theme.palette.warning.main, 0.1),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Cancel sx={{ fontSize: 44, color: 'warning.main' }} />
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={800} color="warning.main" gutterBottom>
          Transfer Cancelled
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No payment was made. Your card was not charged.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/dashboard')}>
          Dashboard
        </Button>
        <Button variant="contained" onClick={() => navigate('/blitzpay')}>
          Try Again
        </Button>
      </Box>
    </Box>
  );
}

// ─── Main exported component ──────────────────────────────────────────────────
export default function T2APaymentSuccess() {
  const [searchParams] = useSearchParams();
  const [status,   setStatus]   = useState('loading');
  const [data,     setData]     = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const hasFired = useRef(false);

  useEffect(() => {
    if (hasFired.current) return;
    hasFired.current = true;

    const sessionId = searchParams.get('session_id');
    const provider  = searchParams.get('provider');
    const type      = searchParams.get('type');

    console.log('[T2APaymentSuccess] params:', { sessionId, provider, type });

    if (!sessionId || provider !== 'stripe' || type !== 't2a_gift') {
      setErrorMsg('Invalid payment parameters. Contact support if you were charged.');
      setStatus('error');
      return;
    }

    // Use the existing API client — it knows the correct base URL,
    // handles auth headers, CORS, and env differences automatically.
    client.balanceLedger.t2aGiftSuccess({ session_id: sessionId })
      .then((result) => {
        console.log('[T2APaymentSuccess] result:', result);
        setData(result);
        setStatus('success');
      })
      .catch((err) => {
        console.error('[T2APaymentSuccess] error:', err);
        const msg = err?.response?.data?.error || err?.message || 'Payment confirmed but crediting failed. Please contact support.';
        setErrorMsg(msg);
        setStatus('error');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status === 'loading') return <T2ALoading />;
  if (status === 'error')   return <T2AError message={errorMsg} />;
  return <T2ASuccess data={data} />;
}